/**
 * runWithUndo — fire a destructive action after a countdown, with a toast
 * that shows the remaining seconds and an "Undo" button to cancel.
 *
 * Built on `react-hot-toast` (already a dep). No extra packages.
 *
 * Usage:
 *   runWithUndo({
 *     label: 'Vault revoked',
 *     delayMs: 10000,
 *     action: async () => { ... actual destructive API call ... },
 *     onCommitted: () => fetchData(),
 *     onUndone: () => toast('Cancelled'),
 *   });
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

// Stable component so React's hooks order doesn't drift when react-hot-toast
// re-invokes the render function on each tick.
const UndoToast = ({ t, label, totalMs, onUndo, testIdPrefix }) => {
  const [remaining, setRemaining] = useState(Math.ceil(totalMs / 1000));

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const handleUndo = (e) => {
    e?.stopPropagation?.();
    onUndo();
    toast.dismiss(t.id);
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg bg-slate-900 border border-slate-700 text-white ${t.visible ? 'animate-in fade-in' : 'animate-out fade-out'}`}
      data-testid={`${testIdPrefix}-toast`}
    >
      <span className="text-sm font-medium" data-testid={`${testIdPrefix}-toast-label`}>
        {label}
        <span className="text-gray-400 ml-2">· in {remaining}s</span>
      </span>
      <button
        type="button"
        onClick={handleUndo}
        className="ml-2 text-xs font-bold text-amber-300 hover:text-amber-200 underline underline-offset-2"
        data-testid={`${testIdPrefix}-undo-btn`}
      >
        Undo
      </button>
    </div>
  );
};

export function runWithUndo({ label, delayMs = 5000, action, onCommitted, onUndone, testIdPrefix = 'undo' }) {
  let cancelled = false;

  const timeoutId = setTimeout(async () => {
    if (cancelled) return;
    toast.dismiss(toastId);
    try {
      await action();
      onCommitted && onCommitted();
    } catch (err) {
      console.error('runWithUndo action failed:', err);
      toast.error(err?.message || 'Action failed');
    }
  }, delayMs);

  const toastId = toast.custom(
    (t) => (
      <UndoToast
        t={t}
        label={label}
        totalMs={delayMs}
        testIdPrefix={testIdPrefix}
        onUndo={() => {
          cancelled = true;
          clearTimeout(timeoutId);
          onUndone && onUndone();
        }}
      />
    ),
    { duration: delayMs + 500 }
  );

  return () => {
    cancelled = true;
    clearTimeout(timeoutId);
    toast.dismiss(toastId);
  };
}

export default runWithUndo;
